import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
    explanation: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const IndiaHeritage: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        setShowExplanation(false);
        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeSocialStudies/IndiaHeritage.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeSocialStudies/IndiaHeritage.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setShowExplanation(false);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
        else {
            setShowExplanation(true);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "India – A Land of Rich Heritage | Class 3 Social Studies (CBSE & ICSE)";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Discover India’s rich heritage through monuments, leaders, folk tales, festivals, and national symbols in this Class 3 Social Studies lesson for CBSE & ICSE students." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <header>
                                <h1 className="mb-5">🌟 Chapter 4 – India: A Land of Rich Heritage</h1>
                            </header>

                            <section>
                                <h2>🏛️ Meaning and Understanding of Heritage</h2>
                                <p>Heritage means the traditions, culture, monuments, and values passed down from one generation to another.
                                    It includes our art, music, literature, languages, buildings, and customs that make India special.</p>
                            </section>

                            <section>
                                <h2>🕌 Buildings and Monuments</h2>
                                <p>India is home to many beautiful and historic monuments. These tell us stories from the past:</p>
                                <ul>
                                    <li>🕌 <b>Taj Mahal</b> – A symbol of love in Agra.</li>
                                    <li>🏰 <b>Red Fort</b> – Built by the Mughal emperors in Delhi.</li>
                                    <li>🕍 <b>Sun Temple</b> – Famous for its unique architecture in Konark.</li>
                                    <li>🛕 <b>Meenakshi Temple</b> – Known for its colorful carvings in Madurai.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>👑 Great National Leaders and Their Achievements</h2>
                                <ul>
                                    <li><b>Mahatma Gandhi</b> – Led India’s non-violent freedom struggle.</li>
                                    <li><b>Jawaharlal Nehru</b> – India’s first Prime Minister who promoted education and unity.</li>
                                    <li><b>Rani Lakshmibai</b> – Brave queen who fought against British rule.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>📖 Epics and Folk Tales</h2>
                                <ul>
                                    <li><b>Panchatantra Tales</b> – Animal stories that teach life lessons.</li>
                                    <li><b>Jataka Tales</b> – Stories about kindness, honesty, and wisdom.</li>
                                    <li><b>Ramayana</b> and <b>Mahabharata</b> – Great Indian epics full of adventures and moral lessons.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>🇮🇳 National Symbols</h2>
                                <ul>
                                    <li><b>National Flag</b> – Tricolour with saffron, white, and green.</li>
                                    <li><b>National Animal</b> – Tiger 🐅</li>
                                    <li><b>National Bird</b> – Peacock 🦚</li>
                                    <li><b>National Flower</b> – Lotus 🌸</li>
                                    <li><b>National Anthem</b> – Jana Gana Mana 🎶</li>
                                </ul>
                            </section>

                            <section>
                                <h2>🎉 Festivals and Community Celebrations</h2>
                                <ul>
                                    <li><b>Diwali</b> – Festival of Lights 🪔</li>
                                    <li><b>Holi</b> – Festival of Colours 🎨</li>
                                    <li><b>Eid</b> – Festival of joy and sharing 🍽️</li>
                                    <li><b>Christmas</b> – Festival of love and giving 🎄</li>
                                    <li><b>Baisakhi, Pongal, Onam</b> – Harvest festivals 🌾</li>
                                </ul>
                            </section>

                            <section>
                                <h2>✅ Quick Recap</h2>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Topic</th>
                                            <th>Key Points</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Heritage</td>
                                            <td>Traditions, culture, monuments, values</td>
                                        </tr>
                                        <tr>
                                            <td>Monuments</td>
                                            <td>Taj Mahal, Red Fort, Sun Temple</td>
                                        </tr>
                                        <tr>
                                            <td>Leaders</td>
                                            <td>Gandhi, Nehru, Rani Lakshmibai</td>
                                        </tr>
                                        <tr>
                                            <td>Stories</td>
                                            <td>Panchatantra, Jataka, Ramayana</td>
                                        </tr>
                                        <tr>
                                            <td>Symbols</td>
                                            <td>Flag, Tiger, Peacock, Lotus</td>
                                        </tr>
                                        <tr>
                                            <td>Festivals</td>
                                            <td>Diwali, Holi, Eid, Christmas</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <header>
                            <h1>🌟 Chapter 4 – India: A Land of Rich Heritage</h1>
                        </header>

                        <section>
                            <h2>🏛️ Meaning and Understanding of Heritage</h2>
                            <p>Heritage means the traditions, culture, monuments, and values passed down from one generation to another.
                                It includes our art, music, literature, languages, buildings, and customs that make India special.</p>
                        </section>

                        <section>
                            <h2>🕌 Buildings and Monuments</h2>
                            <p>India is home to many beautiful and historic monuments. These tell us stories from the past:</p>
                            <ul>
                                <li>🕌 <b>Taj Mahal</b> – A symbol of love in Agra.</li>
                                <li>🏰 <b>Red Fort</b> – Built by the Mughal emperors in Delhi.</li>
                                <li>🕍 <b>Sun Temple</b> – Famous for its unique architecture in Konark.</li>
                                <li>🛕 <b>Meenakshi Temple</b> – Known for its colorful carvings in Madurai.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>👑 Great National Leaders and Their Achievements</h2>
                            <ul>
                                <li><b>Mahatma Gandhi</b> – Led India’s non-violent freedom struggle.</li>
                                <li><b>Jawaharlal Nehru</b> – India’s first Prime Minister who promoted education and unity.</li>
                                <li><b>Rani Lakshmibai</b> – Brave queen who fought against British rule.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>📖 Epics and Folk Tales</h2>
                            <ul>
                                <li><b>Panchatantra Tales</b> – Animal stories that teach life lessons.</li>
                                <li><b>Jataka Tales</b> – Stories about kindness, honesty, and wisdom.</li>
                                <li><b>Ramayana</b> and <b>Mahabharata</b> – Great Indian epics full of adventures and moral lessons.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>🇮🇳 National Symbols</h2>
                            <ul>
                                <li><b>National Flag</b> – Tricolour with saffron, white, and green.</li>
                                <li><b>National Animal</b> – Tiger 🐅</li>
                                <li><b>National Bird</b> – Peacock 🦚</li>
                                <li><b>National Flower</b> – Lotus 🌸</li>
                                <li><b>National Anthem</b> – Jana Gana Mana 🎶</li>
                            </ul>
                        </section>

                        <section>
                            <h2>🎉 Festivals and Community Celebrations</h2>
                            <ul>
                                <li><b>Diwali</b> – Festival of Lights 🪔</li>
                                <li><b>Holi</b> – Festival of Colours 🎨</li>
                                <li><b>Eid</b> – Festival of joy and sharing 🍽️</li>
                                <li><b>Christmas</b> – Festival of love and giving 🎄</li>
                                <li><b>Baisakhi, Pongal, Onam</b> – Harvest festivals 🌾</li>
                            </ul>
                        </section>

                        <section>
                            <h2>✅ Quick Recap</h2>
                            <table>
                                <tr>
                                    <th>Topic</th>
                                    <th>Key Points</th>
                                </tr>
                                <tr>
                                    <td>Heritage</td>
                                    <td>Traditions, culture, monuments, values</td>
                                </tr>
                                <tr>
                                    <td>Monuments</td>
                                    <td>Taj Mahal, Red Fort, Sun Temple</td>
                                </tr>
                                <tr>
                                    <td>Leaders</td>
                                    <td>Gandhi, Nehru, Rani Lakshmibai</td>
                                </tr>
                                <tr>
                                    <td>Stories</td>
                                    <td>Panchatantra, Jataka, Ramayana</td>
                                </tr>
                                <tr>
                                    <td>Symbols</td>
                                    <td>Flag, Tiger, Peacock, Lotus</td>
                                </tr>
                                <tr>
                                    <td>Festivals</td>
                                    <td>Diwali, Holi, Eid, Christmas</td>
                                </tr>
                            </table>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}

                            {(showExplanation) ? (
                                <>
                                <span><strong>Correct option:</strong> {currentQuestion.answer}</span>
                                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}>
                                    </div>
                                    <div className="mt-3 text-center">
                                        <button className="btn hero-btn" onClick={loadRandomSyllabusQuestions}>Next Question</button>
                                    </div>
                                </>
                            ) : (<></>)}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default IndiaHeritage;
